import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
/**
 * AuthCoordinationService manages authentication flow coordination across multiple browser tabs
 * to prevent login redirect loops when multiple tabs detect unauthenticated state simultaneously.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthCoordinationService {
  private readonly STORAGE_KEY = 'auth_login_in_progress';
  private readonly TAB_ID_KEY = 'current_tab_id';
  private currentTabId: string | null = null;

  constructor() {
    this.initTabId();
  }

  /**
   * Initialize tab ID for debugging and coordination
   */
  private initTabId(): void {
    const existingTabId = sessionStorage.getItem(this.TAB_ID_KEY);

    if (existingTabId) {
      this.currentTabId = existingTabId;
    } else {
      const newTabId = uuidv4();
      sessionStorage.setItem(this.TAB_ID_KEY, newTabId);
      this.currentTabId = newTabId;
    }
  }

  /**
   * Get current tab ID
   */
  getCurrentTabId(): string | null {
    return this.currentTabId;
  }

  /**
   * Check if another tab is currently handling login process
   */
  isOtherTabLoggingIn(): boolean {
    try {
      const loginData = localStorage.getItem(this.STORAGE_KEY);
      if (!loginData) return false;

      const { tabId } = JSON.parse(loginData);
      // Compare if the login flag was set by a different tab
      return tabId !== this.currentTabId;
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Set login flag to indicate this tab is handling login
   */
  setLoginFlag(): void {
    try {
      const loginData = {
        timestamp: Date.now(),
        tabId: this.currentTabId
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(loginData));
    } catch (error) {
      console.error('Error setting login flag:', error);
    }
  }

  /**
   * Clear login flag after 3 seconds delay
   */
  clearLoginFlag(): void {
    setTimeout(() => {
      try {
        // Check if key exists before removing
        if (localStorage.getItem(this.STORAGE_KEY)) {
          localStorage.removeItem(this.STORAGE_KEY);
        }
      } catch (error) {
        console.error('Error clearing login flag:', error);
      }
    }, 3000); // 3 seconds delay
  }

  /**
   * Generate random wait time between 20-30 seconds
   */
  getRandomWaitTime(): number {
    return Math.floor(Math.random() * 10000) + 20000; // 20000-30000ms
  }
}
